import { flow, Instance, types } from 'mobx-state-tree';
import { AITasks } from 'http-requests';
import { RequestStateModel } from './general-models';
import { AIRequestCreateDTO } from '../../http-requests/ai-requests';
import { IRequest } from '../../types';

const AiTaskModel = types
  .model({
    ai_tag: types.optional(
      types.enumeration('AiTag', ['ai_assist', 'auto_request']),
      'ai_assist'
    ),
    call_id: types.optional(types.string, ''),
    created_at: types.optional(types.string, ''),
    employee_id: types.optional(types.string, ''),
    employee_number: types.optional(types.string, ''),
    finished_at: types.optional(types.maybeNull(types.string), null),
    id: types.optional(types.identifier, ''),
    request_number: types.optional(types.string, ''),
    status: types.optional(
      types.enumeration('AiTaskStatus', [
        'started',
        'processing',
        'success',
        'canceled',
        'failed',
        'timeout',
      ]),
      'started'
    ),
    uniqid: types.optional(types.string, ''),
  })
  .views((self) => ({
    //...
  }))
  .actions((self) => ({
    changeStatus(
      status:
        | 'started'
        | 'processing'
        | 'success'
        | 'canceled'
        | 'failed'
        | 'timeout'
    ): void {
      self.status = status;
    },
  }));

export interface IAiTaskModel extends Instance<typeof AiTaskModel> {}

export const AIRequestsStore = types
  .model('AIRequestsStore', {
    isAiMode: types.optional(types.boolean, false),
    allTasks: types.optional(types.array(AiTaskModel), []),
    currentTaskId: types.optional(types.maybeNull(types.string), null),
  })
  .volatile(() => ({
    getAllTasksStatus: RequestStateModel.create(),
    createAiRequestStatus: RequestStateModel.create(),
    cancelAiTaskStatus: RequestStateModel.create(),
    createModeratedAiRequestStatus: RequestStateModel.create(),
    recordPointStatus: RequestStateModel.create(),
  }))
  .views((self) => ({
    get activeTasks() {
      return self.allTasks.filter(
        (task) =>
          !['canceled', 'failed', 'timeout', 'success'].includes(task.status)
      );
    },
    get currentTask(): IAiTaskModel | null {
      if (!self.currentTaskId) {
        return null;
      }

      return (
        self.allTasks.find((task) => task.id === self.currentTaskId) || null
      );
    },

    getTaskByCallId(callId: string): IAiTaskModel | null {
      return self.allTasks.find((task) => task.call_id === callId) || null;
    },
  }))
  .actions((self) => ({
    changeTaskStatus(
      taskId: string,
      status:
        | 'started'
        | 'processing'
        | 'success'
        | 'canceled'
        | 'failed'
        | 'timeout'
    ) {
      const task = self.allTasks.find((task) => task.id === taskId);

      if (task) {
        task.changeStatus(status);
      }
    },
    setAiMode(value: boolean) {
      self.isAiMode = value;
    },
    setCurrentTask(value: string) {
      self.currentTaskId = value;
    },
  }))
  .actions((self) => ({
    getAllTasks: flow(function* () {
      try {
        self.getAllTasksStatus.loadingStates();

        const response: {
          count: number;
          items: IAiTaskModel[];
        } = yield AITasks.GetAiTasks();
        self.allTasks.replace(response.items);

        self.getAllTasksStatus.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.getAllTasksStatus.errorStates();
      } finally {
        self.getAllTasksStatus.finallyStates();
      }
    }),
    createAiRequest: flow(function* (data: AIRequestCreateDTO) {
      try {
        self.createAiRequestStatus.loadingStates();

        const response: {
          task: IAiTaskModel;
          request: IRequest | null; //null при ошибке создания таски (202 статус запроса)
        } = yield AITasks.CreateAiRequest(data);

        self.allTasks.push(response.task);

        if (response.task.status !== 'failed') {
          self.setAiMode(true);
          self.currentTaskId = response.task.id;
        }

        self.createAiRequestStatus.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.createAiRequestStatus.errorStates();

        throw e;
      } finally {
        self.createAiRequestStatus.finallyStates();
      }
    }),
    cancelAiTask: flow(function* (taskId: string) {
      try {
        self.cancelAiTaskStatus.loadingStates();

        const response = yield AITasks.CancelAiTask(taskId);

        self.currentTaskId = null;

        self.changeTaskStatus(taskId, 'canceled');

        self.cancelAiTaskStatus.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.cancelAiTaskStatus.errorStates();
      } finally {
        self.cancelAiTaskStatus.finallyStates();
      }
    }),
    createModeratedAiRequest: flow(function* (data: IRequest) {
      try {
        self.createModeratedAiRequestStatus.loadingStates();

        const response: any = yield AITasks.CreateModeratedAiRequest(
          data.number,
          data
        );

        self.createModeratedAiRequestStatus.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.createModeratedAiRequestStatus.errorStates();
        throw e;
      } finally {
        self.createModeratedAiRequestStatus.finallyStates();
      }
    }),
    recordPoint: flow(function* (
      params: Parameters<typeof AITasks.RecordPoint>[0]
    ) {
      try {
        self.recordPointStatus.loadingStates();
        const response = yield AITasks.RecordPoint(params);
        self.recordPointStatus.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.recordPointStatus.errorStates();
      } finally {
        self.recordPointStatus.finallyStates();
      }
    }),
  }));
